import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Slide {
  id: string;
  image: string;
  tag: string;
  title: string;
  desc: string;
  to: string;
  cta: string;
}

const slides: Slide[] = [
  {
    id: 's1',
    image:
      'https://readdy.ai/api/search-image?query=Cinematic%20wide%20banner%20artwork%20in%20deep%20navy%20blue%20and%20charcoal%20tones%20with%20a%20warm%20amber%20light%20horizon%2C%20abstract%20moody%20film%20production%20atmosphere%2C%20soft%20grain%20texture%2C%20elegant%20editorial%20background%20composition%2C%20dark%20enough%20for%20white%20text%20overlay&width=1920&height=1080&seq=hero-01&orientation=landscape',
    tag: '最新作品',
    title: '《时间的皱褶》',
    desc: '钟表修复师澄发现，有些表走的是另一种时间。',
    to: '/works/wrinkles-of-time',
    cta: '查看作品',
  },
  {
    id: 's2',
    image:
      'https://readdy.ai/api/search-image?query=Cinematic%20wide%20banner%20artwork%20in%20deep%20navy%20blue%20and%20charcoal%20tones%20with%20warm%20amber%20starlight%20over%20a%20dark%20observatory%20dome%2C%20moody%20atmospheric%20minimal%20composition%2C%20soft%20grain%20texture%2C%20elegant%20editorial%20background%20with%20strong%20contrast%20for%20text%20overlay&width=1920&height=1080&seq=hero-02&orientation=landscape',
    tag: '口碑之作',
    title: '《遥远的星空》',
    desc: '在山顶天文台，她寻找一颗只存在于论文中的星。',
    to: '/works/distant-stars',
    cta: '查看作品',
  },
  {
    id: 's3',
    image:
      'https://readdy.ai/api/search-image?query=Cinematic%20wide%20banner%20artwork%20in%20deep%20navy%20blue%20and%20charcoal%20tones%20with%20warm%20amber%20lamp%20glow%20in%20a%20dim%20room%2C%20moody%20atmospheric%20minimal%20composition%2C%20soft%20grain%20texture%2C%20elegant%20editorial%20background%20with%20strong%20contrast%20for%20white%20text%20overlay&width=1920&height=1080&seq=hero-03&orientation=landscape',
    tag: '影视动态',
    title: '《沉默的证明》收官',
    desc: '现实题材表达获行业认可，讨论热度持续攀升。',
    to: '/news/n2',
    cta: '阅读动态',
  },
];

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => setCurrent((i) => (i + 1) % slides.length), []);
  const prev = useCallback(() => setCurrent((i) => (i - 1 + slides.length) % slides.length), []);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[440px] w-full md:h-[600px]">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={[
              'absolute inset-0 transition-opacity duration-700',
              index === current ? 'opacity-100' : 'pointer-events-none opacity-0',
            ].join(' ')}
          >
            <img
              src={slide.image}
              alt={slide.title}
              title={`${slide.title} - 吉泽明步影视品牌站`}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/65 to-primary-950/35"></div>
          </div>
        ))}

        <div className="absolute inset-0 flex items-center">
          <div className="container-page">
            <div className="max-w-2xl animate-fadeUp">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent-400/50 bg-accent-500/15 px-3 py-1 text-xs font-medium tracking-wide text-accent-300">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-400"></span>
                {slides[current].tag}
              </span>
              <h2 className="mt-5 font-heading text-3xl font-semibold leading-tight text-background-50 md:text-5xl">
                {slides[current].title}
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                {slides[current].desc}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={slides[current].to}
                  className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-primary-950 whitespace-nowrap transition-colors hover:bg-accent-400 cursor-pointer"
                >
                  {slides[current].cta}
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/works"
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-200/60 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-background-50/10 cursor-pointer"
                >
                  浏览全部作品
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 w-full">
          <div className="container-page flex items-center justify-between">
            <div className="flex items-center gap-2">
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  aria-label={`切换到第 ${index + 1} 张`}
                  onClick={() => setCurrent(index)}
                  className={[
                    'h-1.5 rounded-full transition-all cursor-pointer',
                    index === current ? 'w-8 bg-accent-400' : 'w-3 bg-background-200/50 hover:bg-background-200',
                  ].join(' ')}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={prev}
                aria-label="上一张"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-200/50 text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="下一张"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-200/50 text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}